import React, { useEffect, useState } from "react";
import ToastMessage from "../../../../../common/ToastMessage";
import CustomDataGrid from "../../../../../common/CustomDataGrid";
import useCustomers from "../../../../../store/ReactQueries/customers/useCustomer";
import useAddCustomers from "../../../../../store/ReactQueries/customers/useAddCustomers";
import useUpdateCustomer from "../../../../../store/ReactQueries/customers/useUpdateCustomer";
import useDeleteCustomer from "../../../../../store/ReactQueries/customers/useDeleteCustomer";
import { GridColDef } from "@mui/x-data-grid";
import { PAGESIZE } from "../../../../../utils/constants";
import { FieldErrors } from "../../../../../domain/helpers/Errors";
import { formatDate } from "../../../../../common/GlobalFunctions";
import { validateSchema } from "../../../../../common/ValidateSchema";
import { useDarkModeStore } from "../../../../../store/useDarkModeStore";
import { useLanguageStore } from "../../../../../store/useLanguageStore";
import { CustomerInterface } from "../../../../../models/TicketModule/Customer";
import { customerSchema } from "../../../../../domain/ValidationSchemas/Ticket/customer";

export const Customers = () => {
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGESIZE);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [form, setForm] = useState<CustomerInterface>({
    id: "",
    title: "",
    isDefault: false,
    code: "",
    contractTerms: "",
    companyId: "",
    phoneNo: "",
  });
  const [modal, setModal] = useState<{ add: boolean; edit: boolean; view: boolean }>({
    add: false,
    edit: false,
    view: false,
  });

  let { data: customers, error: customerError, isLoading, refetch } = useCustomers(page, pageSize);

  const addCustomer = useAddCustomers(page, pageSize);
  const updateCustomer = useUpdateCustomer(page, pageSize);
  const deleteCustomer = useDeleteCustomer(page, pageSize);

  useEffect(() => {
    if (customerError) ToastMessage.Failed(`Error: ${customerError.message}`);
  }, [customerError]);

  const columns: GridColDef[] = [
    { field: "id", headerName: "Id", type: "string", editable: false, width: 150 },
    { field: "title", headerName: "Title", type: "string", editable: true, width: 200 },
    {
      field: "code",
      headerName: "Code",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row?.code ?? ""}</span>,
    },
    {
      field: "isDefault",
      headerName: "IsDefault",
      type: "boolean",
      editable: true,
      renderCell: ({ value }) => <span style={{ color: darkMode ? "white" : "black" }}>{value ? `✓` : "✕"}</span>,
    },
    {
      field: "contractTerms",
      headerName: "Contract Terms",
      width: 250,
      type: "custom",
      editable: false,
      renderCell: ({ row }) => <span>{row?.contractTerms ?? ""}</span>,
    },
    { field: "phoneNo", headerName: "PhoneNo", type: "string", editable: true, width: 200 },
    {
      field: "company",
      headerName: "Company",
      width: 200,
      type: "custom",
      editable: false,
      renderCell: ({ row }) => <span>{row.company && row.company.title ? row.company.title : ""}</span>,
    },
    {
      field: "createdDate",
      headerName: "CreatedDate",
      type: "dateTime",
      editable: false,
      width: 200,
      valueFormatter: formatDate,
    },
    {
      field: "updatedDate",
      headerName: "UpdatedDate",
      type: "dateTime",
      editable: false,
      width: 200,
      valueFormatter: formatDate,
    },
    { field: "tenantId", headerName: "TenantId", type: "string", editable: false, width: 150 },
    { field: "authorId", headerName: "AuthorId", type: "string", editable: false, width: 150 },
    { field: "companyId", headerName: "CompanyId", type: "string", editable: false, width: 150 },
    { field: "modifiedById", headerName: "ModifiedById", type: "string", editable: false, width: 150 },
  ];

  const addItems = () => {
    setForm({
      ...form,
      id: "",
      title: "",
      code: "",
      contractTerms: "",
      phoneNo: "",
      isDefault: false,
      companyId: "",
    });
    setModal({ ...modal, add: true });
    setErrors({});
  };

  const handleError = (error: unknown) => {
    if (error instanceof Error) ToastMessage.Failed(error.message);
    else ToastMessage.Failed();
  };

  const handleSave = async () => {
    const isValid = validateSchema(customerSchema, form);

    setErrors(isValid.errors);
    if (!isValid.result) return;

    const { id, title, isDefault, code, contractTerms, companyId, phoneNo } = form;

    const upForm: CustomerInterface = {
      id,
      title,
      isDefault,
      code,
      contractTerms,
      phoneNo,
      companyId,
    };

    if (upForm.id === "") {
      const { id, ...formData } = upForm;

      addCustomer.mutate(formData, {
        onSuccess: () => {
          if (upForm.isDefault) refetch();
          ToastMessage.Success();
        },
        onError: (error) => handleError(error),
      });
    } else {
      updateCustomer.mutate(upForm, {
        onSuccess: () => {
          if (upForm.isDefault) refetch();
          ToastMessage.Success();
        },
        onError: (error) => handleError(error),
      });
    }
    setModal({ ...modal, add: false, edit: false, view: false });
  };

  const handleDelete = async (id: string) => {
    if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
      deleteCustomer.mutate(id, {
        onSuccess: () => ToastMessage.Success(),
        onError: (error) => handleError(error),
      });
    }
  };

  return (
    <CustomDataGrid
      name={language["Customers"]}
      newAddComponent="Customers"
      rows={customers || []}
      columns={columns}
      hideColumns={["authorId", "id", "tenantId", "modifiedById", "code", "companyId"]}
      showColumnModal={["title", "code", "contractTerms", "phoneNo", "isDefault", "companyId"]}
      form={form}
      setForm={setForm}
      errors={errors}
      setErrors={setErrors}
      handleSave={handleSave}
      modal={modal}
      setModal={setModal}
      addItems={addItems}
      handleDelete={handleDelete}
      loading={isLoading}
      page={page}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
    />
  );
};
export default Customers;
